import { Component, Injector, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { signalMethod } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { MatomoTracker } from 'ngx-matomo-client';

import { Role } from '@optical/rbac';
import { Locale } from '@optical/types';
import { DateLocaleId, getDateLocale, setDateLocale } from '@optical/utils';

import { AuthStore, UserSettingsStore } from '@webapp/shared';

@Component({
  selector: 'knx-root',
  imports: [RouterOutlet],
  template: '<router-outlet></router-outlet>',
})
export class RootComponent implements OnInit {
  /**
   * Mapping of `Locale` enums to language IDs understood by ngx-translate. Both English locales
   * share a single catalog — the region only affects formatting, not the words.
   */
  private readonly translationMap = {
    [Locale.DE_DE]: 'de',
    [Locale.EN_GB]: 'en',
    [Locale.EN_US]: 'en',
  } as const;

  /**
   * Mapping of `Locale` enums to date locale IDs.
   */
  private readonly dateLocaleMap: Record<Locale, DateLocaleId> = {
    [Locale.DE_DE]: 'de',
    [Locale.EN_GB]: 'en-gb',
    [Locale.EN_US]: 'en',
  };

  private readonly authStore = inject(AuthStore);
  private readonly injector = inject(Injector);
  private readonly tracker = inject(MatomoTracker);
  private readonly translate = inject(TranslateService);
  private readonly userSettings = inject(UserSettingsStore);

  /**
   * Sets the Matomo role dimension.
   */
  setMatomoRoleDimension = signalMethod<Role[] | null>((roles) => {
    this.tracker.setCustomDimension(1, roles?.at(0) ?? '-');
  });

  /**
   * Sets the Matomo tenant dimension.
   */
  setMatomoTenantDimension = signalMethod<string | null>((tenantId) => {
    this.tracker.setCustomDimension(2, tenantId ?? '-');
  });

  /**
   * Synchronizes the analytics consent with the user settings.
   */
  syncAnalyticsConsent = signalMethod<boolean>((allowUsageAnalytics) => {
    if (allowUsageAnalytics) {
      this.tracker.setConsentGiven();
    } else {
      this.tracker.forgetConsentGiven();
    }
  });

  /**
   * Synchronizes the translation catalog and the date locale with the user settings.
   *
   * Both are derived from the single `locale` setting so the words and the date/number formats can
   * never drift apart.
   */
  syncLocale = signalMethod<Locale>((locale) => {
    const settingsLang = this.translationMap[locale];

    if (this.translate.currentLang() !== settingsLang) {
      this.translate.use(settingsLang);
    }

    const settingsLocale = this.dateLocaleMap[locale];

    if (getDateLocale() !== settingsLocale) {
      setDateLocale(settingsLocale);
    }
  });

  /**
   * @implements {OnInit}
   */
  async ngOnInit(): Promise<void> {
    const fallbackLang = this.translationMap[Locale.EN_US];

    // This language will be used as a fallback when no translation is found in the current language
    this.translate.setFallbackLang(fallbackLang);

    this.syncAnalyticsConsent(this.userSettings.allowUsageAnalytics, { injector: this.injector });
    this.syncLocale(this.userSettings.locale, { injector: this.injector });

    this.setMatomoRoleDimension(this.authStore.roles, { injector: this.injector });
    this.setMatomoTenantDimension(this.authStore.tenantId, { injector: this.injector });
  }
}
